// ===================================================
// useSessionHandlers.js — AI 호출 기반 세션 핸들러 훅
//
// 역할:
//   - 랜딩 화면: 유저 입력 → AI 질문 2개 생성 (handleLandingNext)
//   - QA 화면: '계속 기록하기' → 다음 질문 생성 (handleContinue)
//   - QA 화면: '저장하고 종료' → 키워드 추출 + 저장 (handleEnd)
//   - QA 화면: 질문 재생성 버튼 → 단일 질문 교체 (handleRegenerateQuestion)
//   - 세션 편집: 수정 내용 저장 + 키워드 재추출 (handleUpdateSession)
//   - 나와의 대화: 사용자 메시지 → GPT 답변 (handleChatSend)
//
// 의사결정 모드 전용 동작:
//   - askGPT / continueGPT가 JSON으로 응답 → parseDecisionResponse로 파싱
//   - 각 라운드에서 다룬 사고 축(axes)을 decisionAxes 상태에 누적
//   - 저장 시 discussedAxes + tentativeDecision + nextActionHint 추출
// ===================================================

import { useState, useEffect } from 'react';
import { askGPT, continueGPT, regenerateSingleQuestion, generatePreferenceSummary, extractKeywords, resolveKeywordsWithExisting, summarizeTitle, summarizeContext } from '../api/sessionApi';
import { getAuthToken } from '../api/gptClient';
import { getToday } from '../utils/dateUtils';
import { collectAllKeywords } from '../utils/sessionUtils';
import { chatWithMemory } from '../api/chatApi';
import { searchContextByEmbedding, generateEmbedding } from '../api/embeddingApi';
import { detectSchedule, generateQuickQuestion, addToGoogleCalendar } from '../api/calendarApi';
import { parseQuestions, parseQuestionsWithStrategies, parseKeywords, parseDecisionResponse, validateQuestionsClient, detectRepetition } from '../parseQuestions';
import {
  saveSession, updateSession,
  saveQuestionFeedback, getQuestionFeedbackCount,
  getRecentQuestionFeedback, getPreferenceSummary, upsertPreferenceSummary,
  appendChatMessage, logEvent,
  saveSessionEmbedding,
} from '../storage';
import { saveGuestSession, updateGuestSession } from '../utils/guestStorage';
import { findRelevantSessions, buildAllSessionContexts } from '../utils/contextMatcher';
import { computeAverageAnswerLength, findDeepAnswerQuestions, findResonantAxes } from '../utils/answerAnalyzer';
import { Capacitor } from '@capacitor/core';
import { calculateCurrentStreak, STREAK_MILESTONES } from '../utils/streakCalculator';
import { appRead, appWrite } from '../utils/appStorage';
import { logError } from '../utils/errorLogger';

// 네이티브 모듈은 동적 import()로 웹 번들에서 제외
// 웹에서는 no-op(아무 동작 없이 무시), 네이티브에서만 실제 모듈 로드
const _isNative = Capacitor.isNativePlatform();
const triggerSaveHaptic = () => { if (_isNative) import('../native/haptics').then(m => m.triggerSaveHaptic()); };
const triggerErrorHaptic = () => { if (_isNative) import('../native/haptics').then(m => m.triggerErrorHaptic()); };
const hideKeyboard = () => { if (_isNative) import('../native/keyboard').then(m => m.hideKeyboard()); };
const sendStreakCelebration = (...a) => { if (_isNative) import('../native/notifications').then(m => m.sendStreakCelebration(...a)); };
const cancelStreakDangerPush = () => { if (_isNative) import('../native/notifications').then(m => m.cancelStreakDangerPush()); };
const scheduleDecisionFollowUpPush = (...a) => { if (_isNative) import('../native/notifications').then(m => m.scheduleDecisionFollowUpPush(...a)); };
const scheduleDreamFirstStepFollowUpPush = (...a) => { if (_isNative) import('../native/notifications').then(m => m.scheduleDreamFirstStepFollowUpPush(...a)); };
const sendFirstRecordCelebration = () => { if (_isNative) import('../native/notifications').then(m => m.sendFirstRecordCelebration()); };
const scheduleNewChatMessagePush = (...a) => { if (_isNative) import('../native/notifications').then(m => m.scheduleNewChatMessagePush(...a)); };

export function useSessionHandlers({
  user,
  sessions,
  mode,
  initialText,
  sessionRounds,
  decisionAxes,
  lifeSummary,
  setLifeSummary,
  setError,
  setLoading,
  setInitialText,
  setCurrentQuestions,
  setQuestionsLoading,
  setSessionRounds,
  setDecisionAxes,
  setScreen,
  refreshSessions,
  resetSession,
  showToast,
  // 구글 캘린더 연동 관련
  googleAccessToken,
  selectedCalendarId,
  isCalendarConnected,
  onShowCalendarPrompt,     // 미연동 시 안내 카드 표시 콜백
  onShowQuickQuestion,      // 단순 일정 후 한 줄 질문 카드 표시 콜백
  onOpenChat,               // 저장 후 인사이트 토스트 클릭 시 채팅으로 이동
}) {
  // ── 내부 유틸: 피드백 저장 후 5의 배수 도달 시 선호도 요약 갱신 (백그라운드)
  const recordFeedbackAndMaybeUpdateSummary = (currentMode, questionText, action, strategy = null) => {
    if (!user) return;
    (async () => {
      await saveQuestionFeedback({ mode: currentMode, questionText, action, strategy });
      const count = await getQuestionFeedbackCount(currentMode);
      if (count >= 10 && count % 5 === 0) {
        try {
          const feedback = await getRecentQuestionFeedback(currentMode, 50);
          const accepted     = feedback.filter(f => f.action === 'accepted').map(f => f.question_text);
          const rejected     = feedback.filter(f => f.action === 'rejected').map(f => f.question_text);
          const deepAnswers  = feedback.filter(f => f.action === 'deep_answer').map(f => f.question_text);
          const resonantAxes = feedback.filter(f => f.action === 'resonant_axis').map(f => f.question_text);
          const summary = await generatePreferenceSummary(accepted, rejected, deepAnswers, resonantAxes);
          await upsertPreferenceSummary({ mode: currentMode, summary, feedbackCount: count });
          // 캐시도 함께 갱신 (개선안 3)
          setCachedPreferenceSummary(prev => ({ ...prev, [currentMode]: summary }));
        } catch { /* 선호도 요약 갱신 실패 시 다음 기회에 재시도 */ }
      }
    })().catch(() => {});
  };

  // ── 개선안 3: 선호도 요약 캐시 (앱 시작 시 미리 로드) ──
  // 기존: handleLandingNext/handleContinue 때마다 DB 조회 (150~300ms)
  // 개선: hook 마운트 시 전체 모드 요약을 미리 조회 → 이후 캐시에서 즉시 반환
  const [cachedPreferenceSummary, setCachedPreferenceSummary] = useState({});

  useEffect(() => {
    if (!user) return;
    const modes = ['diary', 'note', 'planner', 'decision'];
    Promise.all(modes.map(async (m) => {
      const data = await getPreferenceSummary(m);
      return [m, data?.summary || null];
    })).then(results => {
      const cache = {};
      results.forEach(([m, summary]) => { cache[m] = summary; });
      setCachedPreferenceSummary(cache);
    }).catch(() => {});
  }, [user]);

  // (fetchPreferenceSummary는 개선안 3에서 캐시로 대체 — 직접 호출 불필요)

  // ── 내부 유틸: 과거 기록 컨텍스트 검색 (C2)
  // 임베딩 기반 의미 유사도 검색을 먼저 시도하고,
  // 실패하거나 결과가 없으면 키워드 매칭으로 폴백합니다.
  const findPastContext = async (text, currentSessions, maxCount, preferMode) => {
    // 로그인 유저에게만 임베딩 검색 적용 (게스트는 키워드 매칭만)
    // 전체 임베딩 검색에 5초 타임아웃 적용 — 초과 시 키워드 매칭으로 폴백
    if (user) {
      try {
        const embeddingResults = await Promise.race([
          searchContextByEmbedding(text, maxCount, preferMode),
          new Promise(resolve => setTimeout(() => resolve(null), 5000)),
        ]);
        if (embeddingResults && embeddingResults.length > 0) return embeddingResults;
      } catch {
        // 임베딩 검색 실패 → 키워드 매칭으로 폴백
      }
    }
    // 폴백: 키워드 교집합 방식
    return findRelevantSessions(text, currentSessions, maxCount, preferMode);
  };

  // ── 내부 유틸: 기록 저장 후 임베딩 생성 및 저장 (C2, 백그라운드)
  // contextSummary(요약)가 있으면 그것을, 없으면 initialText를 임베딩합니다.
  const generateAndSaveEmbedding = (sessionId, embeddingText) => {
    if (!user || !sessionId || !embeddingText) return;
    (async () => {
      try {
        const embedding = await generateEmbedding(embeddingText);
        await saveSessionEmbedding(sessionId, embedding);
      } catch {
        /* 임베딩 저장 실패 시 무시 — 키워드 매칭으로 폴백되므로 기능에 영향 없음 */
      }
    })();
  };

  // 랜딩 화면: 유저 텍스트 입력 → AI가 질문 2개 생성
  // 플래너 모드: 일정 감지를 먼저 수행합니다.
  // 의사결정 모드: JSON 응답이므로 스트리밍 불가 → 기존 방식 유지
  // 그 외 모드: 스트리밍으로 Q1이 완성되는 순간 QA 화면으로 이동, Q2는 이어서 표시
  const handleLandingNext = async (text, selectedMode) => {
    setError('');
    setLoading(true);
    const startTime = Date.now();
    try {
      // ── 플래너 모드: 일정 감지 분기 ────────────────────────
      if (selectedMode === 'planner') {
        const detection = await detectSchedule(text);

        // 일정이 감지되었고 구글 캘린더 미연동 상태 → 안내 카드 표시
        if (detection.events.length > 0 && !isCalendarConnected) {
          if (detection.type === 'simple_schedule') {
            setLoading(false);
            onShowCalendarPrompt?.();
            setInitialText(text);
            return;
          }
        }

        // 단순 일정 + 캘린더 연동 완료 → 캘린더 추가 후 한 줄 질문
        if (detection.type === 'simple_schedule' && detection.events.length > 0 && isCalendarConnected) {
          try {
            await addToGoogleCalendar(detection.events, googleAccessToken, selectedCalendarId);
            const eventNames = detection.events.map(e => e.title).join(', ');
            showToast(`📅 "${eventNames}" 일정이 추가됐어요`);
          } catch {
            showToast('캘린더 추가에 실패했어요', 'error');
          }

          try {
            const quickQ = await generateQuickQuestion(detection.events);
            setInitialText(text);
            setLoading(false);
            onShowQuickQuestion?.(quickQ.trim(), detection.events);
            return;
          } catch {
            setLoading(false);
            return;
          }
        }

        // 복합 계획 + 일정 포함 + 캘린더 연동 → 백그라운드 캘린더 추가
        if (detection.type === 'plan' && detection.events.length > 0 && isCalendarConnected) {
          addToGoogleCalendar(detection.events, googleAccessToken, selectedCalendarId)
            .then(() => {
              const count = detection.events.length;
              showToast(`📅 일정 ${count}건이 캘린더에 추가됐어요`);
            })
            .catch(() => {});
        }
      }

      // ── 질문 생성 → QA 화면 ────────────────────────────────
      // 개선안 1: 컨텍스트 검색을 GPT 호출에서 디커플링
      //   - 첫 질문은 유저 텍스트만으로 즉시 GPT 호출 (컨텍스트 검색 대기 없음)
      //   - 과거 기록 검색은 백그라운드에서 실행 → 2라운드(handleContinue)에서 활용
      //   - 선호도 요약은 개선안 3에서 캐시된 값 사용 (DB 호출 없음)
      const preferenceSummary = cachedPreferenceSummary[selectedMode] || null;

      setInitialText(text);
      setSessionRounds([]);
      setDecisionAxes([]);

      if (selectedMode === 'decision' || selectedMode === 'dream') {
        setQuestionsLoading(true);
        const gptText = await askGPT(text, selectedMode, null, preferenceSummary, lifeSummary);
        const { questions, axes } = parseDecisionResponse(gptText);
        setCurrentQuestions(questions);
        setDecisionAxes(axes);
        setQuestionsLoading(false);
        if (user?.id) logEvent(user.id, 'session_start', { mode: selectedMode });
        setScreen('qa');
      } else {
        // 스트리밍: 화면은 즉시 전환하되, 스켈레톤을 보여준 뒤 완료 후 질문 표시
        setQuestionsLoading(true);
        let screenTransitioned = false;
        const gptText = await askGPT(text, selectedMode, null, preferenceSummary, lifeSummary, (accum) => {
          const partial = parseQuestions(accum);
          if (partial.length > 0) {
            setCurrentQuestions(partial);
            if (!screenTransitioned) {
              screenTransitioned = true;
              setLoading(false);
              if (user?.id) logEvent(user.id, 'session_start', { mode: selectedMode });
              setScreen('qa');
            }
          }
        });
        // 스트리밍 완료 후 최종 파싱 결과로 확정 + 스켈레톤 해제
        const finalQs = parseQuestions(gptText);
        setCurrentQuestions(finalQs);
        setQuestionsLoading(false);
        if (!screenTransitioned) {
          if (user?.id) logEvent(user.id, 'session_start', { mode: selectedMode });
          setScreen('qa');
        }
        // 후처리 4: 스트리밍 후 품질 로깅 (차단 없이 데이터 수집)
        if (user?.id) {
          const { valid, violations } = validateQuestionsClient(finalQs, 2);
          if (!valid) logEvent(user.id, 'question_quality_violation', { mode: selectedMode, violations });
        }
      }

      // 응답 시간 기록
      if (user?.id) logEvent(user.id, 'response_timing', {
        mode: selectedMode,
        type: 'initial',
        total_ms: Date.now() - startTime,
      });
    } catch (err) {
      setError(err.message);
      triggerErrorHaptic();
      logError({
        action: 'handleLandingNext',
        error: err,
        screen: 'landing',
        context: { mode: selectedMode },
        durationMs: Date.now() - startTime,
      });
    } finally {
      setLoading(false);
    }
  };

  // QA 화면: '계속 기록하기' → 다음 질문 2개 생성
  // 의사결정 모드: JSON 응답이므로 스트리밍 불가
  // 그 외 모드: 스트리밍으로 Q1 완성 시 즉시 표시, Q2는 이어서 업데이트
  const handleContinue = async (questions, answers) => {
    setError('');
    setLoading(true);
    const startTime = Date.now();
    try {
      // 답변이 있는 질문은 accepted 피드백으로 기록 (백그라운드, 의사결정 모드 제외)
      // 전략 태그가 있으면 함께 저장
      if (mode !== 'decision') {
        const withStrategies = parseQuestionsWithStrategies(
          questions.map((q, i) => `${i + 1}. ${q}`).join('\n')
        );
        questions.forEach((q, i) => {
          if (answers[i]?.trim()) {
            const strategy = withStrategies[i]?.strategy || null;
            recordFeedbackAndMaybeUpdateSummary(mode, q, 'accepted', strategy);
          }
        });
      }

      const allText = [initialText, ...answers].filter(Boolean).join(' ');

      // C2: 임베딩 검색 (2라운드부터는 과거 맥락 활용)
      // 선호도 요약은 캐시에서 즉시 가져옴 (개선안 3)
      const tCtx = Date.now();
      const pastContext = await findPastContext(allText, sessions, 3, mode);
      const contextMs = Date.now() - tCtx;
      const preferenceSummary = (mode !== 'decision') ? (cachedPreferenceSummary[mode] || null) : null;

      // 이번 세션에서 지금까지 나온 모든 질문 (현재 라운드 포함)
      // → GPT가 의미적으로 비슷한 질문을 반복하지 않도록 전달
      const allPreviousQuestions = [
        ...sessionRounds.flatMap(r => r.questions || []),
        ...questions,
      ];

      // 현재 라운드를 포함한 전체 라운드 히스토리
      // → continueGPT가 대화 전체 흐름을 보고 질문을 만들 수 있도록
      const allRounds = [...sessionRounds, { questions, answers }];

      // 항목 1,6,9: 라운드 번호 및 답변 길이 계산 (대화 깊이 + 소크라테스/시간축 조건)
      const currentRound = allRounds.length; // 1-based (첫 continue는 라운드 1)
      const avgLen = computeAverageAnswerLength(sessions, mode);
      const lastLen = Math.max(...answers.map(a => (a || '').trim().length), 0);

      // sessionRounds에 현재 라운드 추가 (GPT 호출 전에 미리 처리)
      setSessionRounds(prev => [...prev, { questions, answers }]);

      setQuestionsLoading(true);

      if (mode === 'decision' || mode === 'dream') {
        const gptText = await continueGPT(
          initialText, allRounds, mode, pastContext, preferenceSummary, lifeSummary,
          decisionAxes, allPreviousQuestions, null,
          { round: currentRound, avgAnswerLen: avgLen, lastAnswerLen: lastLen },
        );
        const { questions: newQuestions, axes: newAxes, strategies: newStrategies } = parseDecisionResponse(gptText);
        setCurrentQuestions(newQuestions);
        setQuestionsLoading(false);
        setDecisionAxes(prev => [...prev, ...newAxes]);
        // 축 기반 모드 전략 피드백: 이전 라운드 질문의 전략을 accepted로 기록
        if (newStrategies.length > 0) {
          newQuestions.forEach((q, i) => {
            if (newStrategies[i]) {
              recordFeedbackAndMaybeUpdateSummary(mode, q, 'accepted', newStrategies[i]);
            }
          });
        }
      } else {
        // 스트리밍: Q1 완성되면 로딩 해제 후 즉시 표시
        let loadingCleared = false;
        const gptText = await continueGPT(
          initialText, allRounds, mode, pastContext, preferenceSummary, lifeSummary,
          decisionAxes, allPreviousQuestions,
          (accum) => {
            const partial = parseQuestions(accum);
            if (partial.length > 0) {
              if (!loadingCleared) {
                loadingCleared = true;
                setLoading(false);
              }
              setCurrentQuestions(partial);
            }
          },
          { round: currentRound, avgAnswerLen: avgLen, lastAnswerLen: lastLen },
        );
        // 스트리밍 완료 후 전략 태그 추출하여 피드백 기록 (현재 라운드 질문은 다음 continue에서 accepted 처리)
        const finalContinueQs = parseQuestions(gptText);
        setCurrentQuestions(finalContinueQs);
        setQuestionsLoading(false);
        if (!loadingCleared) setLoading(false);

        // 후처리 4: 스트리밍 후 품질 로깅
        if (user?.id) {
          const { valid, violations } = validateQuestionsClient(finalContinueQs, 2);
          if (!valid) logEvent(user.id, 'question_quality_violation', { mode, round: currentRound, violations });
        }
        // 후처리 6: 반복 감지 로깅
        const allPrevQs = sessionRounds.flatMap(r => r.questions || []);
        const repetitions = detectRepetition(finalContinueQs, allPrevQs);
        if (repetitions.length > 0 && user?.id) {
          logEvent(user.id, 'question_repetition', { mode, round: currentRound, repetitions });
        }
      }

      // 응답 시간 기록: 버튼 클릭 → 스트리밍 완료까지 총 시간
      if (user?.id) logEvent(user.id, 'response_timing', {
        mode,
        type: 'continue',
        context_search_ms: contextMs,
        total_ms: Date.now() - startTime,
      });
    } catch (err) {
      setError(err.message);
      triggerErrorHaptic();
      logError({
        action: 'handleContinue',
        error: err,
        screen: 'qa',
        context: { mode },
        durationMs: Date.now() - startTime,
      });
    } finally {
      setLoading(false);
    }
  };

  // QA 화면: 질문 재생성 버튼 → 해당 질문 1개만 새로 생성
  // index: 재생성할 질문의 인덱스 (0 또는 1)
  const handleRegenerateQuestion = async (index, currentQuestions) => {
    const questionToReplace = currentQuestions[index];
    const otherQuestion = currentQuestions[1 - index] || '';

    // rejected 피드백 저장 + 필요 시 선호도 갱신 (백그라운드)
    recordFeedbackAndMaybeUpdateSummary(mode, questionToReplace, 'rejected');

    // 이번 세션에서 이미 나온 모든 질문 (교체 대상 제외) → 유사 질문 방지
    const allPreviousQuestions = [
      ...sessionRounds.flatMap(r => r.questions || []),
      ...currentQuestions.filter((_, i) => i !== index),
    ];

    // 선호도 요약은 캐시에서 즉시 가져옴 (개선안 3)
    const preferenceSummary = cachedPreferenceSummary[mode] || null;
    // 서버에서 정제 + 검증 + 재시도까지 수행하므로 결과를 그대로 사용
    let cleaned = await regenerateSingleQuestion(initialText, mode, otherQuestion, preferenceSummary, allPreviousQuestions);
    cleaned = cleaned.trim();

    // 후처리 6: 반복 감지 — 반복 시 1회 재시도
    const repetitions = detectRepetition([cleaned], allPreviousQuestions);
    if (repetitions.length > 0) {
      const retryResult = await regenerateSingleQuestion(
        initialText, mode, otherQuestion, preferenceSummary,
        [...allPreviousQuestions, cleaned],
      );
      cleaned = retryResult.trim();
    }

    // 해당 인덱스의 질문만 교체
    const newQuestions = [...currentQuestions];
    newQuestions[index] = cleaned;
    setCurrentQuestions(newQuestions);
  };

  // QA 화면: 질문 신고 버튼 → 질문 품질 피드백 DB 기록 (프롬프트 QA용)
  // DB 저장이 확인된 경우에만 성공 토스트를 띄웁니다.
  const handleReportQuestion = async (index, currentQuestions, reason) => {
    const questionText = currentQuestions[index];
    const currentRound = sessionRounds.length + 1;
    try {
      await saveQuestionFeedback({
        mode, questionText,
        action: 'reported',
        reportReason: reason,
        initialText,
        round: currentRound,
        priorRounds: sessionRounds,
      });
      showToast('피드백이 기록됐어요');
    } catch {
      showToast('피드백 저장에 실패했어요. 다시 시도해주세요.', 'error');
    }
  };

  // QA 화면: '저장하고 종료하기' → 키워드 추출 + 저장
  const handleEnd = async (questions, answers) => {
    setLoading(true);
    const saveStart = Date.now();
    // 답변이 있는 질문은 accepted 피드백으로 기록 (백그라운드, 의사결정 모드 제외)
    if (mode !== 'decision') {
      questions.forEach((q, i) => {
        if (answers[i]?.trim()) {
          recordFeedbackAndMaybeUpdateSummary(mode, q, 'accepted');
        }
      });
    }
    const finalRounds = [...sessionRounds, { questions, answers }];

    // ── 암묵적 피드백 저장 ──
    if (user) {
      try {
        const avgLength = computeAverageAnswerLength(sessions, mode);
        const deepQuestions = findDeepAnswerQuestions(finalRounds, avgLength);
        deepQuestions.forEach(q => {
          recordFeedbackAndMaybeUpdateSummary(mode, q, 'deep_answer');
        });
        if (mode === 'decision' || mode === 'dream') {
          const resonantAxes = findResonantAxes(finalRounds, decisionAxes, avgLength);
          resonantAxes.forEach(axis => {
            recordFeedbackAndMaybeUpdateSummary(mode, axis, 'resonant_axis');
          });
        }
      } catch { /* 암묵적 피드백 저장 실패 시 무시 */ }
    }

    // ── 저장: 로그인 유저 → Edge Function, 게스트 → localStorage ──
    try {
      if (user) {
        // /api/save-session Edge Function: GPT 4회 + DB 저장을 서버에서 일괄 처리
        const token = await getAuthToken();

        // 피드백 데이터 수집 (선호도 요약 갱신용)
        const accepted = finalRounds.flatMap(r => r.questions.filter((q, i) => r.answers[i]?.trim()));
        const rejected = []; // 재생성된 질문은 이미 recordFeedbackAndMaybeUpdateSummary에서 처리
        const avgLength = computeAverageAnswerLength(sessions, mode);
        const deepAnswers = findDeepAnswerQuestions(finalRounds, avgLength);
        const resonantAxes = (mode === 'decision' || mode === 'dream') ? findResonantAxes(finalRounds, decisionAxes, avgLength) : [];

        const res = await fetch('/api/save-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            initialText, mode,
            rounds: finalRounds,
            existingKeywords: [...collectAllKeywords(sessions)],
            lifeSummary,
            discussedAxes: (mode === 'decision' || mode === 'dream') ? decisionAxes : undefined,
            questionFeedback: { accepted, rejected, deepAnswers, resonantAxes },
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || '저장에 실패했어요.');
        }
        // 타이밍 기록
        logEvent(user.id, 'save_timing', {
          mode, rounds: finalRounds.length,
          total_ms: Date.now() - saveStart,
        });
        logEvent(user.id, 'session_complete', { mode, rounds: finalRounds.length });

        // 인사이트 메시지가 서버에서 자동 생성되므로 토스트만 표시
        showToast('AI가 기록을 읽고 메시지를 보냈어요', 'success', onOpenChat);
        scheduleNewChatMessagePush();
      } else {
        // 게스트: 기존 방식 유지 (키워드/요약 없이 기본 저장)
        saveGuestSession({ initialText, mode, rounds: finalRounds, keywords: [], title: null, contextSummary: null, decisionMeta: null });
      }
    } catch (saveErr) {
      logError({
        action: 'handleEnd_saveSession',
        error: saveErr,
        screen: 'qa',
        context: { mode, rounds: finalRounds.length },
      });
      triggerErrorHaptic();
      showToast('저장에 실패했어요. 잠시 후 다시 시도해주세요.', 'error');
      setLoading(false);
      return;
    }
    await refreshSessions();
    hideKeyboard();
    triggerSaveHaptic();
    showToast('기록이 저장됐어요');
    setScreen('landing');
    resetSession();

    // 백그라운드 GPT 작업(키워드/제목/요약) 완료 후 사이드바 갱신
    // 5초, 12초 후 자동 새로고침 → 제목이 생기면 사이드바에 반영됨
    if (user) {
      setTimeout(() => refreshSessions(), 5000);
      setTimeout(() => refreshSessions(), 12000);
    }

    // ── [D-1] 스트릭 축하 알림 ──────────────────────────────
    // 방금 저장했으므로 오늘 기록이 있는 상태 — 스트릭 계산 후 마일스톤이면 축하 알림
    try {
      const todayStr = getToday();
      const sessionsWithToday = { ...sessions };
      if (!sessionsWithToday[todayStr]) sessionsWithToday[todayStr] = [];
      sessionsWithToday[todayStr] = [...sessionsWithToday[todayStr], {}]; // 방금 저장한 세션 반영
      const streak = calculateCurrentStreak(sessionsWithToday);
      if (STREAK_MILESTONES.includes(streak)) {
        sendStreakCelebration(streak);
      }
      // 오늘 기록했으므로 스트릭 위기 알림은 취소
      cancelStreakDangerPush();
    } catch {
      // 스트릭 알림 실패 시 무시 (기록 저장에 영향 없음)
    }

    // ── [F] 고민 상담 후속 알림 ─────────────────────────────
    // decision 모드로 저장한 경우, 3일 후에 결과 기록을 유도하는 알림 예약
    if (mode === 'decision') {
      try { scheduleDecisionFollowUpPush(); } catch { /* 무시 */ }
    }

    // ── [G] 꿈과 목표 첫 행동 팔로우업 알림 ─────────────────
    // dream 모드로 저장한 경우, 7일 후에 첫 행동 실행 여부를 되묻는 알림 예약
    if (mode === 'dream') {
      try { scheduleDreamFirstStepFollowUpPush(); } catch { /* 무시 */ }
    }

    // ── [H] 첫 기록 기념 알림 ───────────────────────────────
    // 앱 설치 후 첫 번째 기록이면 즉시 축하 알림 (omsal_first_record_done 플래그로 1회만)
    try {
      const isFirstRecord = Object.keys(sessions).length === 0 &&
                            !appRead('omsal_first_record_done');
      if (isFirstRecord) {
        sendFirstRecordCelebration();
        appWrite('omsal_first_record_done', 'true');
      }
    } catch { /* 무시 */ }
  };

  // 세션 수정: 편집 내용 저장 + 키워드 재추출 + 컨텍스트 요약 재생성
  const handleUpdateSession = async (date, session, newText, newRounds) => {
    setLoading(true);
    let keywords = session.keywords || [];
    let contextSummary = session.contextSummary || null;

    try {
      const rawKeywords = parseKeywords(await extractKeywords(newText, newRounds));
      keywords = await resolveKeywordsWithExisting(rawKeywords, [...collectAllKeywords(sessions)]);
    } catch { /* 키워드 재추출 실패 시 기존 키워드 유지 */ }

    try { contextSummary = await summarizeContext(newText, newRounds); }
    catch { /* 실패 시 기존 요약 유지 */ }

    // 로그인: DB 수정 / 게스트: 로컬스토리지 수정
    if (user) {
      await updateSession(session.id, { initialText: newText, rounds: newRounds, keywords, contextSummary });
    } else {
      updateGuestSession(session.id, { initialText: newText, rounds: newRounds, keywords, contextSummary });
    }
    await refreshSessions();
    hideKeyboard();      // 수정 저장 후 키보드 닫기
    triggerSaveHaptic(); // 저장 완료 햅틱 피드백
    showToast('수정이 저장됐어요');
    setLoading(false);
  };

  // 나와의 대화: 사용자 메시지 → 과거 기록 기반 GPT 답변
  // 페이지를 이탈해도 백그라운드에서 API 호출이 완료되면 DB에 저장합니다.
  // onComplete 콜백: ChatView가 아직 마운트 중이면 실시간 UI 업데이트용
  const handleChatSend = async (userMessage, chatHistory, onComplete) => {
    const sessionContexts = buildAllSessionContexts(sessions);

    // API 호출을 App 레벨에서 실행 — ChatView 언마운트와 무관하게 진행
    chatWithMemory(userMessage, sessionContexts, chatHistory)
      .then(reply => {
        const assistantMsg = { role: 'assistant', text: reply, referencedCount: sessionContexts.length };
        // DB에 직접 저장 (ChatView가 언마운트되어도 반드시 실행)
        appendChatMessage(assistantMsg);
        // ChatView가 아직 살아있으면 UI 업데이트
        if (onComplete) onComplete(assistantMsg);
      })
      .catch(() => {
        const errorMsg = { role: 'assistant', text: '오류가 발생했어요. 다시 시도해주세요.', isError: true };
        if (onComplete) onComplete(errorMsg);
      });

    return { referencedCount: sessionContexts.length };
  };

  // ── 단순 일정 한 줄 질문 답변 저장 ─────────────────────────
  // 캘린더에 일정 추가 후 보여준 한 줄 질문에 답변했을 때 호출
  const handleQuickQuestionSave = async (question, answer) => {
    setLoading(true);
    const finalRounds = [{ questions: [question], answers: [answer] }];
    let keywords = [];
    let contextSummary = null;

    try {
      const rawKeywords = parseKeywords(await extractKeywords(initialText, finalRounds));
      keywords = await resolveKeywordsWithExisting(rawKeywords, [...collectAllKeywords(sessions)]);
    } catch { /* 키워드 추출 실패 시 빈 배열 */ }

    try { contextSummary = await summarizeContext(initialText, finalRounds); }
    catch { /* 요약 실패 시 null */ }

    if (user) {
      const sessionId = await saveSession({ initialText, mode: 'planner', rounds: finalRounds, keywords, contextSummary });
      // C2: 임베딩 생성 후 저장 (백그라운드)
      generateAndSaveEmbedding(sessionId, contextSummary || initialText);
    } else {
      saveGuestSession({ initialText, mode: 'planner', rounds: finalRounds, keywords, contextSummary });
    }
    await refreshSessions();
    hideKeyboard();
    triggerSaveHaptic();
    showToast('기록이 저장됐어요');
    resetSession();
    setLoading(false);
  };

  // ── 자유 기록 저장 ─────────────────────────────────────────
  // AI 질문 없이 유저가 입력한 텍스트를 직접 저장 (다이어리 / 노트 모드)
  // 저장 시 키워드 + 요약(contextSummary) + 제목을 AI로 추출
  // userTitle: 유저가 직접 입력한 제목 (없으면 null → 노트 모드는 AI 자동 생성)
  const handleFreewriteSave = async (text, selectedMode, userTitle) => {
    setError('');
    setLoading(true);
    const emptyRounds = [];
    let keywords = [];
    let title = userTitle || null;
    let contextSummary = null;

    // 키워드 추출 (기존 키워드와 통합)
    try {
      const rawKeywords = parseKeywords(await extractKeywords(text, emptyRounds));
      keywords = await resolveKeywordsWithExisting(rawKeywords, [...collectAllKeywords(sessions)]);
    } catch { /* 키워드 추출 실패 시 빈 배열 */ }

    // 유저가 제목을 입력하지 않은 경우 → 노트 모드는 AI가 자동 생성
    if (!title && selectedMode === 'note') {
      try { title = await summarizeTitle(text, emptyRounds); }
      catch { /* 제목 요약 실패 시 null */ }
    }

    // 컨텍스트 요약 생성 (나와의 대화에서 활용)
    try { contextSummary = await summarizeContext(text, emptyRounds); }
    catch { /* 요약 실패 시 null */ }

    // ── [H] 첫 기록 기념 알림 (저장 전에 체크: 아직 sessions가 비어 있을 때)
    try {
      const isFirstRecord = Object.keys(sessions).length === 0 &&
                            !appRead('omsal_first_record_done');
      if (isFirstRecord) {
        sendFirstRecordCelebration();
        appWrite('omsal_first_record_done', 'true');
      }
    } catch { /* 무시 */ }

    // 로그인: DB 저장 / 게스트: 로컬스토리지 저장
    if (user) {
      const sessionId = await saveSession({ initialText: text, mode: selectedMode, rounds: emptyRounds, keywords, title, contextSummary });
      // H0 가설 검증: 자유 기록도 세션 완료로 기록 (다이어리/노트는 session_start 없이 바로 저장)
      logEvent(user.id, 'session_complete', { mode: selectedMode, rounds: 0 });
      // C2: 임베딩 생성 후 저장 (백그라운드)
      generateAndSaveEmbedding(sessionId, contextSummary || text);
    } else {
      saveGuestSession({ initialText: text, mode: selectedMode, rounds: emptyRounds, keywords, title, contextSummary });
    }
    await refreshSessions();
    hideKeyboard();
    triggerSaveHaptic();
    showToast('기록이 저장됐어요');
    setScreen('landing');
    resetSession();
    setLoading(false);
  };

  // ── 즐겨찾기 토글 ─────────────────────────────────────────
  // sessionId: 대상 기록의 ID, newPinned: 변경할 즐겨찾기 상태(true/false)
  const handleTogglePinned = async (sessionId, newPinned) => {
    if (user) {
      await updateSession(sessionId, { pinned: newPinned });
    } else {
      updateGuestSession(sessionId, { pinned: newPinned });
    }
    refreshSessions();
  };

  return { handleLandingNext, handleContinue, handleEnd, handleRegenerateQuestion, handleReportQuestion, handleUpdateSession, handleChatSend, handleQuickQuestionSave, handleFreewriteSave, handleTogglePinned };
}
